import React from "react";
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";
import { ImagePicker } from "../Offers/proposal/SlideInspector";
import TwinPicker from "../Offers/proposal/TwinPicker";
import { REPORT_BLOCKS, newBlock } from "./reportOps";

const input = "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none";

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <label className="block text-xs font-medium text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const IconButton = ({ title, onClick, children, danger, disabled }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
    className={`rounded p-1.5 disabled:opacity-30 ${danger ? "text-slate-500 hover:bg-red-50 hover:text-red-600" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
    {children}
  </button>
);

const Check = ({ label, checked, onChange, disabled }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700">
    <input type="checkbox" disabled={disabled} checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
    {label}
  </label>
);

const BlockSettings = ({ block, set }) => {
  switch (block.type) {
    case "text":
      return (
        <Field label="Style">
          <select className={input} value={block.style} onChange={(event) => set({ style: event.target.value })}>
            <option value="plain">Texte courant</option>
            <option value="box">Encart bleu pâle (à retenir)</option>
            <option value="note">Mention grise</option>
          </select>
        </Field>
      );
    case "image":
      return (
        <div className="space-y-3">
          <ImagePicker value={block.image} label="Figure" onChange={(ref) => set({ image: ref })} />
          <Field label={`Largeur : ${block.width} %`}>
            <input type="range" min={30} max={100} step={5} value={block.width} className="w-full"
              onChange={(event) => set({ width: Number(event.target.value) })} />
          </Field>
          <p className="text-xs text-slate-500">La légende est numérotée « Figure n » à l'impression. Sur l'image sélectionnée :
            fond pro, retouche décrite, recadrage (Ctrl+Z revient à l'image d'avant).</p>
        </div>
      );
    case "table": {
      const columns = block.rows[0].length;
      return (
        <div className="flex flex-wrap gap-2 text-xs">
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.rows.length >= 60}
            onClick={() => set({ rows: [...block.rows, Array(columns).fill("")] })}>+ ligne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.rows.length <= 1}
            onClick={() => set({ rows: block.rows.slice(0, -1) })}>− ligne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={columns >= 8}
            onClick={() => set({ rows: block.rows.map((row) => [...row, ""]) })}>+ colonne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={columns <= 1}
            onClick={() => set({ rows: block.rows.map((row) => row.slice(0, -1)) })}>− colonne</button>
        </div>
      );
    }
    case "figures":
      return (
        <div className="flex flex-wrap gap-2 text-xs">
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.items.length >= 4}
            onClick={() => set({ items: [...block.items, { value: "…", label: "…", detail: "" }] })}>+ chiffre</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.items.length <= 1}
            onClick={() => set({ items: block.items.slice(0, -1) })}>− chiffre</button>
        </div>
      );
    case "twin":
      return (
        <div className="space-y-3">
          {/* Autre jumeau : l'image fixe choisie pour l'ancien ne vaut plus */}
          <TwinPicker value={block.embed} onChange={(ref) => set(ref === block.embed ? { embed: ref } : { embed: ref, poster: null })} />
          <Field label={`Hauteur : ${block.height} px`}>
            <input type="range" min={160} max={700} step={10} value={block.height} className="w-full"
              onChange={(event) => set({ height: Number(event.target.value) })} />
          </Field>
          <div className="space-y-1 rounded-lg border border-slate-200 p-2">
            <span className="text-xs font-semibold text-slate-600">Image fixe (PDF)</span>
            <ImagePicker value={block.poster} label="Image" onChange={(ref) => set({ poster: ref })} />
          </div>
        </div>
      );
    default:
      return null;
  }
};

// Réglages de la section ou du bloc sélectionné, ajout de blocs.
const ReportInspector = ({
  section, block, editable, onSection, onBlock, onAddBlock, onMoveBlock, onDuplicateBlock, onRemoveBlock, onDeselect,
}) => {
  if (!section) return <p className="text-sm text-slate-500">Cliquez une section ou un bloc du rapport.</p>;
  if (block) {
    const label = REPORT_BLOCKS.find((item) => item.type === block.type)?.label || block.type;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button type="button" onClick={onDeselect} className="text-xs text-slate-500 hover:text-slate-800">← Section</button>
          <div className="flex">
            <IconButton title="Monter le bloc" onClick={() => onMoveBlock(-1)} disabled={!editable}><ArrowUp className="h-4 w-4" /></IconButton>
            <IconButton title="Descendre le bloc" onClick={() => onMoveBlock(1)} disabled={!editable}><ArrowDown className="h-4 w-4" /></IconButton>
            <IconButton title="Dupliquer le bloc" onClick={onDuplicateBlock} disabled={!editable}><Copy className="h-4 w-4" /></IconButton>
            <IconButton title="Supprimer le bloc" danger onClick={onRemoveBlock} disabled={!editable}><Trash2 className="h-4 w-4" /></IconButton>
          </div>
        </div>
        <Section title={label}>
          {editable ? <BlockSettings block={block} set={onBlock} /> : <p className="text-xs text-slate-500">Lecture seule.</p>}
        </Section>
        <p className="text-xs text-slate-500">Les textes se modifient directement dans le rapport. Une ligne « - … » devient une puce, **…** met en gras.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <Section title="Section">
        <Field label="Niveau">
          <select className={input} disabled={!editable} value={section.level} onChange={(event) => onSection({ level: Number(event.target.value) })}>
            <option value={1}>Section (1, 2…)</option>
            <option value={2}>Sous-section (1.1, 1.2…)</option>
          </select>
        </Field>
        <Check label="Annexe (A, B…)" checked={section.annex} disabled={!editable} onChange={(annex) => onSection({ annex })} />
        <Check label="Commencer sur une nouvelle page" checked={section.page_break} disabled={!editable}
          onChange={(value) => onSection({ page_break: value })} />
        <Check label="Masquer (ni affichée ni imprimée)" checked={section.hidden} disabled={!editable}
          onChange={(hidden) => onSection({ hidden })} />
      </Section>
      {editable && (
        <Section title="Ajouter un bloc">
          <div className="grid grid-cols-1 gap-1">
            {REPORT_BLOCKS.map((item) => (
              <button key={item.type} type="button" onClick={() => onAddBlock(newBlock(item.type))}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100">
                <Plus className="h-3.5 w-3.5 text-slate-400" /> {item.label}
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default ReportInspector;
